/**
 * Fuzzy search utilities for handling spelling mistakes in search queries
 */

/**
 * Calculate Levenshtein distance between two strings
 * Lower distance = more similar
 */
export const levenshteinDistance = (str1: string, str2: string): number => {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  if (s1 === s2) return 0;
  if (s1.length === 0) return s2.length;
  if (s2.length === 0) return s1.length;

  const matrix: number[][] = [];

  // Initialize matrix
  for (let i = 0; i <= s1.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= s2.length; j++) {
    matrix[0][j] = j;
  }

  // Fill matrix
  for (let i = 1; i <= s1.length; i++) {
    for (let j = 1; j <= s2.length; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // deletion
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return matrix[s1.length][s2.length];
};

/**
 * Calculate similarity ratio between two strings (0-1)
 * Higher = more similar
 */
export const stringSimilarity = (str1: string, str2: string): number => {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  if (s1 === s2) return 1;
  
  const maxLength = Math.max(s1.length, s2.length);
  if (maxLength === 0) return 1;
  
  const distance = levenshteinDistance(s1, s2);
  return 1 - distance / maxLength;
};

/**
 * Check if a string contains another string with fuzzy tolerance
 */
export const fuzzyContains = (text: string, query: string, threshold = 0.7): boolean => {
  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();
  
  // Exact match
  if (textLower.includes(queryLower)) return true;
  
  // For very short queries, require exact match
  if (queryLower.length <= 2) return textLower.includes(queryLower);
  
  // Check each word in text
  const textWords = textLower.split(/\s+/);
  for (const word of textWords) {
    if (stringSimilarity(word, queryLower) >= threshold) {
      return true;
    }
    // Also check if word starts similarly
    if (word.length >= queryLower.length) {
      const wordStart = word.substring(0, queryLower.length + 1);
      if (stringSimilarity(wordStart, queryLower) >= threshold) {
        return true;
      }
    }
  }
  
  // Check sliding window for partial matches within text
  const windowSize = queryLower.length;
  for (let i = 0; i <= textLower.length - windowSize; i++) {
    const window = textLower.substring(i, i + windowSize);
    if (stringSimilarity(window, queryLower) >= threshold) {
      return true;
    }
  }
  
  return false;
};

/**
 * Calculate relevance score for a listing based on search query
 * Higher score = more relevant
 */
interface ListingForSearch {
  id: string;
  title: string;
  description: string;
  car_brand: string;
  car_model: string;
  part_number?: string | null;
}

export const calculateRelevanceScore = (
  listing: ListingForSearch,
  searchTokens: string[]
): number => {
  let totalScore = 0;
  const matchedTokens = new Set<string>();
  
  for (const token of searchTokens) {
    const tokenLower = token.toLowerCase();
    let tokenScore = 0;
    
    // Title matches (highest weight)
    const titleLower = listing.title.toLowerCase();
    if (titleLower.includes(tokenLower)) {
      tokenScore = Math.max(tokenScore, 100); // Exact match in title
      matchedTokens.add(token);
    } else if (fuzzyContains(listing.title, token, 0.75)) {
      tokenScore = Math.max(tokenScore, 70); // Fuzzy match in title
      matchedTokens.add(token);
    }
    
    // Brand matches (high weight)
    const brandLower = listing.car_brand.toLowerCase();
    if (brandLower === tokenLower) {
      tokenScore = Math.max(tokenScore, 95); // Exact brand match
      matchedTokens.add(token);
    } else if (brandLower.includes(tokenLower)) {
      tokenScore = Math.max(tokenScore, 85);
      matchedTokens.add(token);
    } else if (stringSimilarity(brandLower, tokenLower) >= 0.7) {
      tokenScore = Math.max(tokenScore, 65); // Fuzzy brand match
      matchedTokens.add(token);
    }
    
    // Model matches (high weight)
    const modelLower = listing.car_model.toLowerCase();
    if (modelLower === tokenLower) {
      tokenScore = Math.max(tokenScore, 90);
      matchedTokens.add(token);
    } else if (modelLower.includes(tokenLower)) {
      tokenScore = Math.max(tokenScore, 80);
      matchedTokens.add(token);
    } else if (stringSimilarity(modelLower, tokenLower) >= 0.7) {
      tokenScore = Math.max(tokenScore, 60);
      matchedTokens.add(token);
    }
    
    // Part number matches
    if (listing.part_number) {
      const partNumLower = listing.part_number.toLowerCase();
      if (partNumLower.includes(tokenLower) || tokenLower.includes(partNumLower)) {
        tokenScore = Math.max(tokenScore, 85);
        matchedTokens.add(token);
      }
    }
    
    // Description matches (lower weight)
    if (tokenScore === 0) {
      const descLower = listing.description.toLowerCase();
      if (descLower.includes(tokenLower)) {
        tokenScore = 40; // Exact match in description
        matchedTokens.add(token);
      } else if (fuzzyContains(listing.description, token, 0.8)) {
        tokenScore = 25; // Fuzzy match in description
        matchedTokens.add(token);
      }
    }
    
    totalScore += tokenScore;
  }
  
  // Bonus for matching all tokens
  if (matchedTokens.size === searchTokens.length && searchTokens.length > 1) {
    totalScore *= 1.3;
  }
  
  // Penalty if not all tokens matched
  const matchRatio = matchedTokens.size / searchTokens.length;
  totalScore *= matchRatio;
  
  return totalScore;
};

/**
 * Filter and rank listings based on fuzzy search
 */
export const fuzzySearchListings = <T extends ListingForSearch>(
  listings: T[],
  query: string,
  minScore = 20
): T[] => {
  if (!query.trim()) return listings;
  
  const searchTokens = query.trim().split(/\s+/).filter(t => t.length > 0);
  if (searchTokens.length === 0) return listings;
  
  // Calculate scores and filter
  const scoredListings = listings
    .map(listing => ({
      listing,
      score: calculateRelevanceScore(listing, searchTokens)
    }))
    .filter(item => item.score >= minScore)
    .sort((a, b) => b.score - a.score);
  
  return scoredListings.map(item => item.listing);
};

/**
 * Common Swedish car brand typos and corrections
 */
const brandCorrections: Record<string, string[]> = {
  'volvo': ['volov', 'vovlo', 'vlovo', 'wolvo'],
  'saab': ['saap', 'sabb', 'saabb', 'sab'],
  'volkswagen': ['folkwagen', 'volskwagen', 'volkswagon', 'vw'],
  'mercedes': ['mercedez', 'mersedes', 'mercedes-benz', 'merc'],
  'toyota': ['toyoda', 'tyoata', 'tooyta', 'toyata'],
  'bmw': ['bm', 'bmv', 'bemw'],
  'audi': ['aoudi', 'audu', 'audii'],
  'ford': ['frod', 'fodr'],
  'peugeot': ['peugot', 'peugeut', 'pugeot', 'pejo'],
  'renault': ['renualt', 'renalt', 'reno'],
  'opel': ['oppel', 'opal'],
  'skoda': ['scoda', 'škoda'],
  'mazda': ['masda', 'mazada'],
  'nissan': ['nisan', 'nissann'],
  'honda': ['hunda', 'hondu'],
  'hyundai': ['hyunday', 'hundai', 'hyundaii'],
  'kia': ['kija', 'kiia'],
  'fiat': ['fiaat', 'fiat'],
  'citroen': ['citroën', 'citrön', 'citroen'],
  'mitsubishi': ['mitsibishi', 'mitsubisi', 'mitsu'],
  'subaru': ['subaruu', 'subaro'],
  'suzuki': ['susuki', 'suzuuki'],
  'lexus': ['leksus', 'lexuss'],
  'porsche': ['porshe', 'porche'],
  'jaguar': ['jaguuar', 'jagaur'],
  'jeep': ['jeeep', 'jepp'],
  'chevrolet': ['chevy', 'chevrolett', 'shevrolet'],
  'seat': ['seet', 'seatt'],
  'alfa romeo': ['alfa', 'alfaromeo'],
};

/**
 * Expand search query with possible corrections
 */
export const expandSearchQuery = (query: string): string[] => {
  const tokens = query.toLowerCase().trim().split(/\s+/);
  const expanded: string[] = [query];
  
  for (const token of tokens) {
    // Check if token might be a misspelled brand
    for (const [correct, typos] of Object.entries(brandCorrections)) {
      if (typos.includes(token) || stringSimilarity(token, correct) >= 0.7) {
        // Add version with corrected brand
        const correctedQuery = tokens
          .map(t => (t === token ? correct : t))
          .join(' ');
        if (!expanded.includes(correctedQuery)) {
          expanded.push(correctedQuery);
        }
      }
    }
  }
  
  return expanded;
};
